import React, { useState, useEffect } from "react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import SettingsTabsNavigation from "./SettingsTabsNavigation";
import { Image, Upload, Trash2, Plus } from "lucide-react";
import { FaRegEye as FaEye, FaRegEyeSlash as FaEyeSlash } from "react-icons/fa";
import { addonService } from "../../../services/addonService";
import { showApiMessage } from "../../../utils/messageHandler";
import toastUtils from "../../../utils/toast";
import AddonsSettings from "./AddonsSettings";
import BuildingAddressSettings from "./BuildingAddressSettings";
import PaymentSetupSettings from "./PaymentSetupSettings";
import RolesPage from "./Roles";
import IntegrationsSettings from "./IntegrationsSettings";
import { useLocation } from "react-router-dom";
import apiV2Service, { organizationApiService } from "../../../services/apiV2Service";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import * as localService from "../../../services/localService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../shadcn/ui/dialog";
import { Button } from "../../../shadcn/ui/button";
import { Textarea } from "../../../shadcn/ui/textarea";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import CountryCurrencyDisplay from "../../../components/CountryCurrencyDisplay";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import logger from "../../../utils/logger";


const HostSettings = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showExposeModal, setShowExposeModal] = useState(false);
  const [showConsumeModal, setShowConsumeModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState(null);
  const location = useLocation();
  const isIntegrationsTab = location.pathname.startsWith("/host/settings-integrations");
  const isAddonsTab = location.pathname === "/host/settings-addons";
  const isBuildingAddressesTab = location.pathname === "/host/settings-building-addresses";
  const isPaymentSetupTab = location.pathname === "/host/settings-payment-setup";
  const isRolesTab = location.pathname === "/host/roles-settings";

  // Form state for creating/editing addons
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    unitPrice: "",
    type: "NON_SUBSCRIPTION",
    serviceCategory: "WORK",
    serviceType: "",
    applicableUserCategories: ["HOST", "B2B"],
    duration: "HOURLY",
    maximumValidity: 24,
    numberOfInstances: 1,
    availableSlots: [
      { day: "MONDAY", startTime: "09:00:00", endTime: "17:00:00" },
      { day: "WEDNESDAY", startTime: "09:00:00", endTime: "17:00:00" },
      { day: "FRIDAY", startTime: "10:00:00", endTime: "15:00:00" },
    ],
  });

  // Profile state
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    msisdn: "",
    imageUrl: "",
    username: "",
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [isProfilePhoneInvalid, setIsProfilePhoneInvalid] = useState(false);

  // Organization state
  const [organization, setOrganization] = useState({
    id: null,
    name: "",
    code: "",
    contactEmail: "",
    alternateEmail: "",
    contactMsisdn: "",
    address: "",
    city: "",
    region: "",
    logoUrl: "",
    domain: "",
    webUrl: "",
    status: "",
    type: "",
    country: "",
  });
  const [organizationLoading, setOrganizationLoading] = useState(true);
  const [organizationError, setOrganizationError] = useState("");
  const [organizationSaving, setOrganizationSaving] = useState(false);
  const [organizationSuccess, setOrganizationSuccess] = useState(false);
  const [showOrganizationEdit, setShowOrganizationEdit] = useState(false);
  const [_userOrganizationId, setUserOrganizationId] = useState(null);
  const [isOrgPhoneInvalid, setIsOrgPhoneInvalid] = useState(false);

  // Booking policy state
  const [minAdvanceBookingHours, setMinAdvanceBookingHours] = useState("");
  const [bookingPolicyLoading, setBookingPolicyLoading] = useState(true);
  const [bookingPolicySaving, setBookingPolicySaving] = useState(false);
  const [bookingPolicyError, setBookingPolicyError] = useState("");

  // Role-based permissions
  const user = localService.getUser();
  const userRole = user?.organizationRole?.code || user?.role || "";
  const canEditSettings = ["ROLE_HOST", "ROLE_SUBSCRIBER_HOST"].includes(userRole);

  // Change password state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [passwordPolicyErrors, setPasswordPolicyErrors] = useState([]);

  // Delete account state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Fetch addons on component mount
  useEffect(() => {
    logger.log("useEffect: fetching addons...");
    fetchAddons();
  }, []);

  // Fetch profile on mount (organization will be fetched after getting user data)
  useEffect(() => {
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch password policy on mount
  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      try {
        const response = await apiV2Service.getDefaultPasswordPolicy();
        if (response.status === 200) {
          setPasswordPolicy(response.data.data);
        }
      } catch (error) {
        logger.error("Failed to fetch password policy:", error);
      }
    };
    fetchPasswordPolicy();
  }, []);

  // Fetch booking policy on mount
  useEffect(() => {
    const fetchBookingPolicy = async () => {
      try {
        setBookingPolicyLoading(true);
        setBookingPolicyError("");
        const response = await spaceManagementService.getBookingPolicy();
        if (response.status === 200 && response.data) {
          setMinAdvanceBookingHours(
            response.data.minAdvanceBookingHours !== undefined && response.data.minAdvanceBookingHours !== null
              ? response.data.minAdvanceBookingHours.toString()
              : ""
          );
        }
      } catch (error) {
        logger.error("Failed to fetch booking policy:", error);
        setBookingPolicyError("Failed to load booking policy");
      } finally {
        setBookingPolicyLoading(false);
      }
    };
    fetchBookingPolicy();
  }, []);

  const handleBookingPolicySave = async () => {
    const hours = parseInt(minAdvanceBookingHours, 10);
    if (isNaN(hours) || hours < 0) {
      setBookingPolicyError("Please enter a valid number of hours (0 or greater).");
      return;
    }

    setBookingPolicySaving(true);
    setBookingPolicyError("");
    try {
      const response = await spaceManagementService.setBookingPolicy({ minAdvanceBookingHours: hours });
      if (response.status === 200 || response.status === 201) {
        setMinAdvanceBookingHours(hours.toString());
        showApiMessage(response, "update booking policy");
      } else {
        showApiMessage(response, "update booking policy");
      }
    } catch (error) {
      logger.error("Error updating booking policy:", error);
      showApiMessage(error, "update booking policy");
    } finally {
      setBookingPolicySaving(false);
    }
  };

  const handleBookingPolicyClear = async () => {
    setBookingPolicySaving(true);
    setBookingPolicyError("");
    try {
      const response = await spaceManagementService.clearBookingPolicy();
      if (response.status === 200 || response.status === 204) {
        setMinAdvanceBookingHours("");
        showApiMessage(response, "clear booking policy");
      } else {
        showApiMessage(response, "clear booking policy");
      }
    } catch (error) {
      logger.error("Error clearing booking policy:", error);
      showApiMessage(error, "clear booking policy");
    } finally {
      setBookingPolicySaving(false);
    }
  };

  const fetchAddons = async () => {
    try {
      setLoading(true);
      const response = await addonService.searchAddons({ size: 100 });
      logger.log("fetchAddons response:", response);
      if (response.status === 200) {
        setAddons(response.data.content || response.data || []);
      } else {
        showApiMessage(response, "fetch addons");
      }
    } catch (error) {
      logger.error("Error fetching addons:", error);
      showApiMessage(error, "fetch addons");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    setProfileLoading(true);
    setProfileError("");
    setProfileSuccess(false);
    try {
      const response = await apiV2Service.getSelfUser();
      const user = response.data;
      logger.log("User data:", user); // Debug log
      setProfile({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        emailAddress: user.emailAddress || "",
        msisdn: user.msisdn || "",
        imageUrl: user.imageUrl || "",
        username: user.username || "",
      });

      // Get organization ID from user data - check different possible field names
      const organizationId = user.organizationId || user.organization?.id || user.organizationId;
      logger.log("Organization ID found:", organizationId); // Debug log

      if (organizationId) {
        setUserOrganizationId(organizationId);
        // Fetch organization details after getting the ID
        fetchOrganization(organizationId);
      } else {
        logger.log("No organization ID found in user data");
        setOrganizationError("No organization ID found in user profile");
        setOrganizationLoading(false);
      }
    } catch (error) {
      setProfileError("Failed to load profile");
      showApiMessage(error, "load profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCreateAddon = async () => {
    try {
      const payload = {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice),
        maximumValidity: parseInt(formData.maximumValidity),
        numberOfInstances: parseInt(formData.numberOfInstances),
      };

      const response = await addonService.createAddon(payload);

      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, "create addon");
        resetForm();
        setShowCreateForm(false);
        fetchAddons();
      } else {
        showApiMessage(response, "create addon");
      }
    } catch (error) {
      logger.error("Error creating addon:", error);
      showApiMessage(error, "create addon");
    }
  };

  const handleUpdateAddon = async () => {
    if (!editingAddon) return;

    try {
      const payload = {
        ...formData,
        unitPrice: parseFloat(formData.unitPrice),
        maximumValidity: parseInt(formData.maximumValidity),
        numberOfInstances: parseInt(formData.numberOfInstances),
      };

      const response = await addonService.updateAddon(editingAddon.id, payload);

      if (response.status === 200) {
        showApiMessage(response, "update addon");
        resetForm();
        setEditingAddon(null);
        fetchAddons();
      } else {
        showApiMessage(response, "update addon");
      }
    } catch (error) {
      logger.error("Error updating addon:", error);
      showApiMessage(error, "update addon");
    }
  };

  const handleDeleteAddon = addonId => {
    setAddonToDelete(addonId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAddon = async () => {
    if (!addonToDelete) return;

    try {
      const response = await addonService.deleteAddon(addonToDelete);

      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "delete addon");
        fetchAddons();
      } else {
        showApiMessage(response, "delete addon");
      }

      setShowDeleteConfirm(false);
      setAddonToDelete(null);
    } catch (error) {
      logger.error("Error deleting addon:", error);
      showApiMessage(error, "delete addon");
      setShowDeleteConfirm(false);
      setAddonToDelete(null);
    }
  };

  const startEdit = addon => {
    setFormData({
      name: addon.name,
      description: addon.description || "",
      unitPrice: addon.unitPrice.toString(),
      type: addon.type,
      serviceCategory: addon.serviceCategory,
      serviceType: addon.serviceType,
      applicableUserCategories: addon.applicableUserCategories,
      duration: addon.duration,
      maximumValidity: addon.maximumValidity,
      numberOfInstances: addon.numberOfInstances,
      availableSlots: addon.availableSlots || [],
    });
    setEditingAddon(addon);
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      unitPrice: "",
      type: "NON_SUBSCRIPTION",
      serviceCategory: "WORK",
      serviceType: "",
      applicableUserCategories: ["HOST", "B2B"],
      duration: "HOURLY",
      maximumValidity: 24,
      numberOfInstances: 1,
      availableSlots: [
        { day: "MONDAY", startTime: "09:00:00", endTime: "17:00:00" },
        { day: "WEDNESDAY", startTime: "09:00:00", endTime: "17:00:00" },
        { day: "FRIDAY", startTime: "10:00:00", endTime: "15:00:00" },
      ],
    });
    setEditingAddon(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setProfileSuccess(false);
  };

  const handleProfileSave = async () => {
    if (isProfilePhoneInvalid) {
      toastUtils.error("Please provide a valid phone number");
      return;
    }

    setProfileSaving(true);
    setProfileError("");
    setProfileSuccess(false);
    try {
      // Create update payload without username field
      const profileUpdateData = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        emailAddress: profile.emailAddress,
        msisdn: profile.msisdn,
        imageUrl: profile.imageUrl,
      };
      const response = await apiV2Service.updateSelfUser(profileUpdateData);
      setProfileSuccess(true);
      showApiMessage(response, "update profile");

      // Update localStorage with the new profile data
      const updatedUserData = localService.getUser();
      if (updatedUserData) {
        const newUserData = { ...updatedUserData, ...profileUpdateData };
        localStorage.setItem("currentUser", JSON.stringify(newUserData));
        logger.log("🔍 [ProfileSettings] Updated localStorage with:", newUserData);
      }

      // Dispatch custom event to notify sidebar of profile update
      window.dispatchEvent(new CustomEvent("profileUpdated"));
    } catch (error) {
      showApiMessage(error, "update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileImageUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProfileImage(true);
    try {
      const response = await spaceManagementService.uploadMedia([file]);
      if ((response.status === 200 || response.status === 201) && response.data && response.data.length > 0) {
        const newImageUrl = response.data[0];
        // Update local state immediately
        setProfile(prev => ({ ...prev, imageUrl: newImageUrl }));
        showApiMessage(response, "upload profile image");

        // Immediately update profile with new imageUrl - exclude username from update
        const profileUpdateData = {
          firstName: profile.firstName,
          lastName: profile.lastName,
          emailAddress: profile.emailAddress,
          msisdn: profile.msisdn,
          imageUrl: newImageUrl,
        };
        const updateResp = await apiV2Service.updateSelfUser(profileUpdateData);
        setProfileSuccess(true);
        showApiMessage(updateResp, "update profile");

        // Update localStorage with the new profile data
        const updatedUserData = localService.getUser();
        if (updatedUserData) {
          const newUserData = { ...updatedUserData, ...profileUpdateData };
          localStorage.setItem("currentUser", JSON.stringify(newUserData));
          logger.log("🔍 [ProfileSettings] Updated localStorage with image:", newUserData);
        }

        // Dispatch custom event to notify sidebar of profile update
        window.dispatchEvent(new CustomEvent("profileUpdated"));
      } else {
        showApiMessage(response, "upload profile image");
      }
    } catch (error) {
      showApiMessage(error, "upload profile image");
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    setUploadingProfileImage(true);
    try {
      const profileUpdateData = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        emailAddress: profile.emailAddress,
        msisdn: profile.msisdn,
        imageUrl: "",
      };
      const response = await apiV2Service.updateSelfUser(profileUpdateData);
      setProfile(prev => ({ ...prev, imageUrl: "" }));
      setProfileSuccess(true);
      showApiMessage(response, "delete profile image");

      // Update localStorage with the new profile data
      const updatedUserData = localService.getUser();
      if (updatedUserData) {
        const newUserData = { ...updatedUserData, ...profileUpdateData };
        localStorage.setItem("currentUser", JSON.stringify(newUserData));
      }

      // Dispatch custom event to notify sidebar of profile update
      window.dispatchEvent(new CustomEvent("profileUpdated"));
    } catch (error) {
      showApiMessage(error, "delete profile image");
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const fetchOrganization = async organizationId => {
    logger.log("fetchOrganization called with ID:", organizationId); // Debug log

    if (!organizationId) {
      logger.log("No organization ID provided");
      setOrganizationError("No organization ID available");
      setOrganizationLoading(false);
      return;
    }

    setOrganizationLoading(true);
    setOrganizationError("");
    setOrganizationSuccess(false);
    try {
      logger.log("Making API call to getOrganizationById with ID:", organizationId);
      const response = await organizationApiService.getOrganizationById(organizationId);
      logger.log("Organization API response:", response); // Debug log
      const orgData = response.data?.data || response.data;
      setOrganization({
        id: orgData.id || null,
        name: orgData.name || "",
        code: orgData.code || "",
        contactEmail: orgData.contactEmail || "",
        alternateEmail: orgData.alternateEmail || "",
        contactMsisdn: orgData.contactMsisdn || "",
        address: orgData.address || "",
        city: orgData.city || "",
        region: orgData.region || "",
        logoUrl: orgData.logoUrl || "",
        domain: orgData.domain || "",
        webUrl: orgData.webUrl || "",
        status: orgData.status || "",
        type: orgData.type || "",
        country: orgData.country || "",
      });
    } catch (error) {
      logger.error("Error fetching organization:", error); // Debug log
      setOrganizationError("Failed to load organization details");
      showApiMessage(error, "load organization");
    } finally {
      setOrganizationLoading(false);
    }
  };

  const handleOrganizationChange = (field, value) => {
    setOrganization(prev => ({ ...prev, [field]: value }));
    setOrganizationSuccess(false);
  };

  const handleOrganizationSave = async e => {
    e.preventDefault();

    if (isOrgPhoneInvalid) {
      setOrganizationError("Please provide a valid contact phone number");
      return;
    }

    setOrganizationSaving(true);
    setOrganizationError("");
    setOrganizationSuccess(false);
    try {
      const updatePayload = {
        name: organization.name,
        code: organization.code,
        contactEmail: organization.contactEmail,
        alternateEmail: organization.alternateEmail,
        contactMsisdn: organization.contactMsisdn,
        address: organization.address,
        city: organization.city,
        region: organization.region,
        logoUrl: organization.logoUrl,
        domain: organization.domain,
        webUrl: organization.webUrl,
      };

      const response = await organizationApiService.updateSelfOrganization(updatePayload);
      setOrganizationSuccess(true);
      setShowOrganizationEdit(false);
      showApiMessage(response, "update organization");
    } catch (error) {
      setOrganizationError("Failed to update organization details");
      showApiMessage(error, "update organization");
    } finally {
      setOrganizationSaving(false);
    }
  };

  const _toggleOrganizationEdit = () => {
    setShowOrganizationEdit(!showOrganizationEdit);
    setOrganizationError("");
    setOrganizationSuccess(false);
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setPasswordError("");
  };

  const handleChangePassword = async e => {
    e.preventDefault();
    setPasswordError("");
    setPasswordPolicyErrors([]);

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    // Apply password policy validation if available
    if (passwordPolicy && passwordData.newPassword) {
      const errors = [];

      if (passwordData.newPassword.length < passwordPolicy.minLength) {
        errors.push(`Password must be at least ${passwordPolicy.minLength} characters.`);
      }
      if (passwordPolicy.maxLength > 0 && passwordData.newPassword.length > passwordPolicy.maxLength) {
        errors.push(`Password must be no more than ${passwordPolicy.maxLength} characters.`);
      }
      if (passwordPolicy.minUppercaseCount > 0) {
        const uppercaseCount = (passwordData.newPassword.match(/[A-Z]/g) || []).length;
        if (uppercaseCount < passwordPolicy.minUppercaseCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minUppercaseCount} uppercase letter(s).`);
        }
      }
      if (passwordPolicy.minLowercaseCount > 0) {
        const lowercaseCount = (passwordData.newPassword.match(/[a-z]/g) || []).length;
        if (lowercaseCount < passwordPolicy.minLowercaseCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minLowercaseCount} lowercase letter(s).`);
        }
      }
      if (passwordPolicy.minDigitCount > 0) {
        const digitCount = (passwordData.newPassword.match(/\d/g) || []).length;
        if (digitCount < passwordPolicy.minDigitCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minDigitCount} digit(s).`);
        }
      }
      if (passwordPolicy.minSymbolCount > 0) {
        const symbolCount = (passwordData.newPassword.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length;
        if (symbolCount < passwordPolicy.minSymbolCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minSymbolCount} special character(s).`);
        }
      }

      if (errors.length > 0) {
        setPasswordPolicyErrors(errors);
        return;
      }
    } else if (passwordData.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await apiV2Service.updateSelfPassword({
        password: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "update password");
        setShowChangePasswordModal(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        showApiMessage(response, "update password");
      }
    } catch (error) {
      setPasswordError("Failed to update password. Please check your current password.");
      showApiMessage(error, "update password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const response = await apiV2Service.deleteUserAccount();
      if (response.status === 204 || response.status === 200) {
        localStorage.clear();
        window.location.href = "/signin";
      }
    } catch (error) {
      showApiMessage(error, "delete account");
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteAccountModal(false);
    }
  };

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          isIntegrationsTab && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowConsumeModal(true)}>
                <Plus className="w-4 h-4" />
                Connect Spaces
              </Button>
              <Button variant="spaciadefault" onClick={() => setShowExposeModal(true)}>
                <Plus className="w-4 h-4" />
                Share Spaces
              </Button>
            </div>
          )
        }
      />
      <SettingsTabsNavigation />
      {isIntegrationsTab ? (
        <IntegrationsSettings
          showExposeModal={showExposeModal}
          showConsumeModal={showConsumeModal}
          setShowExposeModal={setShowExposeModal}
          setShowConsumeModal={setShowConsumeModal}
        />
      ) : isAddonsTab ? (
        <AddonsSettings />
      ) : isBuildingAddressesTab ? (
        <BuildingAddressSettings />
      ) : isPaymentSetupTab ? (
        <PaymentSetupSettings />
      ) : isRolesTab ? (
        <RolesPage />
      ) : (
        <>
          {/* Profile Settings */}
          <h2 className="mx-3 mb-4 text-xl font-semibold text-gray-800">Profile Settings</h2>
          <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            {profileLoading ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <div className="block mb-1 text-sm font-medium text-gray-700">Profile Image</div>
                  <div className="flex flex-col gap-4 items-start sm:flex-row sm:items-center">
                    <div className="w-20 h-20 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="w-32 h-10 bg-gray-200 rounded-md animate-pulse"></div>
                  </div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              </div>
            ) : profileError ? (
              <div className="py-8 text-center text-red-600">{profileError}</div>
            ) : (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleProfileSave();
                }}
                className="grid grid-cols-1 gap-6 md:grid-cols-2"
              >
                <div className="md:col-span-2">
                  <label className="block mb-1 text-sm font-medium text-gray-700">Profile Image</label>
                  <div className="flex flex-col gap-4 items-start sm:flex-row sm:items-center">
                    <label className="flex relative justify-center items-center w-20 h-20 text-gray-400 bg-gray-100 rounded-lg border-2 border-gray-300 border-dashed transition cursor-pointer hover:border-blue-400">
                      {profile.imageUrl ? (
                        <img src={profile.imageUrl} alt="Profile" className="object-cover w-20 h-20 rounded-lg" />
                      ) : (
                        <Image className="w-8 h-8" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProfileImageUpload}
                        disabled={uploadingProfileImage}
                      />
                      {uploadingProfileImage && (
                        <div className="flex absolute inset-0 justify-center items-center bg-white bg-opacity-70 rounded-lg">
                          <span className="text-xs text-gray-600">Uploading...</span>
                        </div>
                      )}
                    </label>
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <label className="flex gap-2 items-center px-4 py-2 w-max text-sm font-medium bg-white rounded-md border border-gray-300 cursor-pointer hover:bg-gray-50">
                          <Upload size={17} />
                          <span>{profile.imageUrl ? "Change Photo" : "Upload Photo"}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleProfileImageUpload}
                            disabled={uploadingProfileImage}
                          />
                        </label>
                        {profile.imageUrl && (
                          <button
                            type="button"
                            onClick={handleDeleteProfileImage}
                            disabled={uploadingProfileImage}
                            className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-red-600 bg-white rounded-md border border-red-200 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={17} />
                            <span>Remove</span>
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Recommended: 512x512px, PNG or JPG</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={e => handleProfileChange("firstName", e.target.value)}
                    className="p-2 w-full text-sm bg-gray-100 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={e => handleProfileChange("lastName", e.target.value)}
                    className="p-2 w-full text-sm bg-gray-100 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Username</label>
                  <input
                    type="text"
                    value={profile.username || ""}
                    disabled
                    className="p-2 w-full text-sm bg-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
                    placeholder="Username not available"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                    {localService.getUser()?.emailConfirmed ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 border border-green-200">
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        Unverified
                      </span>
                    )}
                  </div>
                  <input
                    type="email"
                    value={profile.emailAddress}
                    onChange={e => handleProfileChange("emailAddress", e.target.value)}
                    className="p-2 w-full text-sm bg-gray-100 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Phone Number</label>
                  <div className="phone-input-wrapper">
                    <PhoneInput
                      international
                      limitMaxLength={true}
                      defaultCountry="KE"
                      value={profile.msisdn}
                      onChange={value => {
                        handleProfileChange("msisdn", value || "");
                        setIsProfilePhoneInvalid(value ? !isValidPhoneNumber(value) : false);
                      }}
                      className={`phone-input ${isProfilePhoneInvalid ? "phone-input-error border-red-500" : ""}`}
                      inputProps={{ maxLength: 16 }}
                    />
                  </div>
                  {isProfilePhoneInvalid && <p className="mt-1 text-sm text-red-500">Valid phone number is required</p>}
                </div>
                <CountryCurrencyDisplay />
                <div className="flex gap-2 justify-end md:col-span-2 mt-6">
                  <button
                    type="submit"
                    disabled={profileSaving}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {profileSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Security Settings */}
          <h2 className="mx-3 mt-8 mb-4 text-xl font-semibold text-gray-800">Security Settings</h2>
          <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-md font-medium text-gray-900">Password</h3>
                <p className="text-sm text-gray-500">Change your account password securely.</p>
              </div>
              <Button onClick={() => setShowChangePasswordModal(true)}>Change Password</Button>
            </div>
          </div>

          {/* Organization Settings */}
          <h2 className="mx-3 mt-8 mb-4 text-xl font-semibold text-gray-800">Organization Settings</h2>
          <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            {organizationLoading ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <div className="block mb-1 h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-12 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="block mb-1 h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
                <div className="md:col-span-2">
                  <div className="block mb-1 h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-20 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              </div>
            ) : organizationError ? (
              <div className="py-8 text-center text-red-600">{organizationError}</div>
            ) : (
              <form onSubmit={handleOrganizationSave} className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Company Name</label>
                  <input
                    type="text"
                    value={organization.name}
                    onChange={e => handleOrganizationChange("name", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    required
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Company Code</label>
                  <input
                    type="text"
                    value={organization.code}
                    onChange={e => handleOrganizationChange("code", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    required
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Domain</label>
                  <input
                    type="text"
                    value={organization.domain}
                    onChange={e => handleOrganizationChange("domain", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    placeholder="e.g., company.com"
                    required
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Contact Email</label>
                  <input
                    type="email"
                    value={organization.contactEmail}
                    onChange={e => handleOrganizationChange("contactEmail", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    required
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Alternate Email</label>
                  <input
                    type="email"
                    value={organization.alternateEmail}
                    onChange={e => handleOrganizationChange("alternateEmail", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Contact Phone</label>
                  <div
                    className={`phone-input-wrapper ${!canEditSettings ? "opacity-75 grayscale-[0.5] cursor-not-allowed" : ""}`}
                  >
                    <PhoneInput
                      international
                      limitMaxLength={true}
                      defaultCountry="KE"
                      value={organization.contactMsisdn}
                      onChange={value => {
                        if (!canEditSettings) return;
                        handleOrganizationChange("contactMsisdn", value || "");
                        setIsOrgPhoneInvalid(value ? !isValidPhoneNumber(value) : false);
                      }}
                      className={`phone-input ${isOrgPhoneInvalid ? "phone-input-error border-red-500" : ""} ${!canEditSettings ? "cursor-not-allowed" : ""}`}
                      inputProps={{ maxLength: 16 }}
                      disabled={!canEditSettings}
                    />
                  </div>
                  {isOrgPhoneInvalid && <p className="mt-1 text-sm text-red-500">Valid contact number is required</p>}
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Website URL</label>
                  <input
                    type="text"
                    value={organization.webUrl}
                    onChange={e => handleOrganizationChange("webUrl", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    placeholder="e.g., https://example.com"
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    value={organization.city}
                    onChange={e => handleOrganizationChange("city", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    disabled={!canEditSettings}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Region</label>
                  <input
                    type="text"
                    value={organization.region}
                    onChange={e => handleOrganizationChange("region", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    disabled={!canEditSettings}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 text-sm font-medium text-gray-700">Address</label>
                  <Textarea
                    value={organization.address}
                    onChange={e => handleOrganizationChange("address", e.target.value)}
                    className={`p-2 w-full text-sm rounded-lg min-h-[80px] ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    placeholder="Enter full address"
                    disabled={!canEditSettings}
                  />
                </div>
                {canEditSettings && (
                  <div className="flex gap-2 justify-end md:col-span-2 mt-6">
                    <button
                      type="submit"
                      disabled={organizationSaving}
                      className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {organizationSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Booking Policy Settings */}
          <h2 className="mx-3 mt-8 mb-4 text-xl font-semibold text-gray-800">Booking Policy</h2>
          <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            {bookingPolicyLoading ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <div className="block mb-1 h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="p-2 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              </div>
            ) : (
              <>
                <div className="max-w-md">
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Minimum Advance Booking Notice (hours)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={minAdvanceBookingHours}
                    onChange={e => {
                      setMinAdvanceBookingHours(e.target.value);
                      setBookingPolicyError("");
                    }}
                    className={`p-2 w-full text-sm rounded-lg ${!canEditSettings ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-gray-100"}`}
                    placeholder="e.g., 24"
                    disabled={!canEditSettings}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum number of hours in advance that bookings must be made. Leave empty to clear the policy.
                  </p>
                  {bookingPolicyError && <p className="mt-1 text-sm text-red-500">{bookingPolicyError}</p>}
                </div>
                {canEditSettings && (
                  <div className="flex gap-2 justify-end mt-6">
                    {minAdvanceBookingHours !== "" && (
                      <button
                        type="button"
                        onClick={handleBookingPolicyClear}
                        disabled={bookingPolicySaving}
                        className="px-4 py-2 text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50"
                      >
                        {bookingPolicySaving ? "Saving..." : "Clear Policy"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleBookingPolicySave}
                      disabled={bookingPolicySaving}
                      className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {bookingPolicySaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Danger Zone - Delete Account */}
          <h2 className="mx-3 mt-8 mb-4 text-xl font-semibold text-red-600">Danger Zone</h2>
          <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-red-200 shadow-sm">
            <h3 className="text-base font-semibold text-red-600">Delete Account</h3>
            <p className="mt-1 text-sm text-gray-500">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <button
              onClick={() => setShowDeleteAccountModal(true)}
              className="mt-4 px-4 py-2 text-red-600 border border-red-600 rounded-md hover:bg-red-50 font-medium text-sm"
            >
              Delete Account
            </button>
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Addon</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this addon? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setAddonToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmDeleteAddon} className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Modal */}
      <Dialog open={showDeleteAccountModal} onOpenChange={setShowDeleteAccountModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Account</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete your account? This action cannot be undone. All your data will be
              permanently removed.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowDeleteAccountModal(false)} disabled={isDeletingAccount}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={isDeletingAccount}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingAccount ? "Deleting..." : "Delete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modern Phone Input Styling */}
      <style jsx="true" global="true">{`
        .phone-input-wrapper .PhoneInput {
          background-color: #f3f4f6 !important;
          border: 1px solid transparent !important;
          border-radius: 0.5rem !important;
          padding: 0.5rem 0.75rem !important;
          font-size: 0.875rem !important;
          transition:
            border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out !important;
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
        }

        .phone-input-wrapper .PhoneInput:focus-within {
          outline: none !important;
          border-color: #3b82f6 !important;
          background-color: white !important;
        }

        .phone-input-wrapper .PhoneInput.phone-input-error {
          border-color: #ef4444 !important;
          background-color: #fef2f2 !important;
        }

        .phone-input-wrapper .PhoneInputInput {
          background-color: transparent !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          font-size: 0.875rem !important;
          color: #4b5563 !important;
          outline: none !important;
          flex: 1 !important;
        }

        .phone-input-wrapper .PhoneInputCountry {
          margin-right: 0.5rem !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
          display: flex !important;
          align-items: center !important;
          flex-shrink: 0 !important;
        }

        .phone-input-wrapper .PhoneInputCountrySelect {
          color: #111827 !important;
          font-size: 0.875rem !important;
          background: transparent !important;
          border: none !important;
          outline: none !important;
          cursor: pointer !important;
        }
      `}</style>
      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword}>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={e => handlePasswordInputChange("currentPassword", e.target.value)}
                    placeholder="Enter current password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={e => handlePasswordInputChange("newPassword", e.target.value)}
                    placeholder="Enter new password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={e => handlePasswordInputChange("confirmPassword", e.target.value)}
                    placeholder="Confirm new password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {passwordError && <p className="text-sm text-red-500">{passwordError}</p>}
              {passwordPolicyErrors.length > 0 && (
                <div className="text-sm text-red-500 space-y-1">
                  {passwordPolicyErrors.map((error, index) => (
                    <p key={index}>{error}</p>
                  ))}
                </div>
              )}
              {passwordPolicy && (
                <div className="text-xs text-gray-500 border-t pt-3 mt-2">
                  <p className="font-medium mb-1">Password requirements:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Minimum {passwordPolicy.minLength} characters</li>
                    {passwordPolicy.minUppercaseCount > 0 && (
                      <li>At least {passwordPolicy.minUppercaseCount} uppercase letter(s)</li>
                    )}
                    {passwordPolicy.minLowercaseCount > 0 && (
                      <li>At least {passwordPolicy.minLowercaseCount} lowercase letter(s)</li>
                    )}
                    {passwordPolicy.minDigitCount > 0 && <li>At least {passwordPolicy.minDigitCount} digit(s)</li>}
                    {passwordPolicy.minSymbolCount > 0 && (
                      <li>At least {passwordPolicy.minSymbolCount} special character(s)</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordError("");
                  setPasswordPolicyErrors([]);
                  setShowCurrentPassword(false);
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                  setPasswordData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isChangingPassword}
                className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white"
              >
                {isChangingPassword ? "Saving..." : "Save Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </HPageContainer>
  );
};

export default HostSettings;
